import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { useStore } from 'core/store/';
import { track } from 'core/helpers/analytics';
import { AssociationDiscountModal } from 'common/components/farm/association-discount-modal';

/**
 * Offer-side wrapper for the shared MO Farm Associations modal: persists the three Formik fields
 * the CFM rater reads and mirrors the discount into the offer store so the discounts-card badge and
 * the re-rate override logic stay in sync. The dialog itself lives in common/components/farm because
 * the policy Details card applies the same discount (#34739).
 */
const FarmAssociationDiscountModal = observer(() => {
  const { offer: offerStore } = useStore();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const state = offerStore.offer?.quote?.correctedAddress?.state;

  const setMemberships = useCallback(
    (farmAssociationDiscount, memberOfCattlemenAssociation, memberOfSoybeanAssociation) => {
      setFieldValue('global.farmAssociationDiscount', farmAssociationDiscount);
      setFieldTouched('global.farmAssociationDiscount');
      setFieldValue('global.memberOfCattlemenAssociation', memberOfCattlemenAssociation);
      setFieldTouched('global.memberOfCattlemenAssociation');
      setFieldValue('global.memberOfSoybeanAssociation', memberOfSoybeanAssociation);
      setFieldTouched('global.memberOfSoybeanAssociation');
      offerStore.setIncludeAssociationDiscount(farmAssociationDiscount);
    },
    [setFieldValue, setFieldTouched, offerStore]
  );

  const onClose = useCallback(() => offerStore.setShowFarmAssociationModal(false), [offerStore]);

  const onApply = useCallback(
    ({ memberOfCattlemenAssociation, memberOfSoybeanAssociation }) => {
      setMemberships(true, memberOfCattlemenAssociation, memberOfSoybeanAssociation);
      track('Association Discount Added', {
        discount: 'MO Farm Associations',
        offerId: offerStore.offer?.id,
        state
      });
    },
    [setMemberships, offerStore, state]
  );

  const onRemove = useCallback(() => {
    setMemberships(false, false, false);
    track('Association Discount Removed', {
      discount: 'MO Farm Associations',
      offerId: offerStore.offer?.id,
      state
    });
  }, [setMemberships, offerStore, state]);

  return (
    <AssociationDiscountModal
      isActive={!!offerStore?.includeAssociationDiscount}
      memberships={{
        memberOfCattlemenAssociation: values?.global?.memberOfCattlemenAssociation ?? false,
        memberOfSoybeanAssociation: values?.global?.memberOfSoybeanAssociation ?? false
      }}
      onApply={onApply}
      onRemove={onRemove}
      onClose={onClose}
    />
  );
});

export default FarmAssociationDiscountModal;
